import { Component, Injector, Input, OnInit, computed, inject, signal } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { finalize } from 'rxjs';
import { PayableDocument, ApDocumentLine } from '../../../../models/ap-document/ap-document';
import { ApDocumentProxyService } from '../../../../proxies/ap-document.proxy.service';
import { EnumService } from '../../../../services/enum.service';

/**
 * Read-only "what is this document?" quick view (#4789). Any screen that holds nothing but an ApDocument id
 * — a payment request line, an ap-payment allocation, a cash-request line, a PO settlement row — can show
 * what the document actually is without navigating away and losing the work in progress.
 *
 * <p>Fed by <c>GET {id}/payable-detail</c>, which already returns the document together with its Credit/Debit
 * Notes and the withholding applied by posted Retenciones, so the full adjusted state costs one call. That
 * endpoint fetches by id with no payable filter, so it also resolves documents that have dropped out of the
 * open payables (fully paid, settled) — exactly the rows that most need explaining.</p>
 *
 * <p>The AP control account is shown deliberately: local and international providers post to different AP
 * accounts, so "which account did this land in" is the question a user has when reconciling what a payment or
 * remittance is being applied toward.</p>
 *
 * <p>Purely informational — it closes with no result, and callers do not need to subscribe.</p>
 */
@Component({
  selector: 'app-ap-document-quick-view-modal',
  templateUrl: './ap-document-quick-view-modal.component.html',
  styleUrl: './ap-document-quick-view-modal.component.scss',
})
export class ApDocumentQuickViewModalComponent extends BaseModalComponent<null> implements OnInit {
  private readonly documentService = inject(ApDocumentProxyService);
  private readonly enumService = inject(EnumService);

  /** The AP document to display. */
  @Input({ required: true }) documentId!: string;

  readonly payable = signal<PayableDocument | null>(null);
  readonly loading = signal(true);
  readonly failed = signal(false);

  /**
   * The lines grouped per PO, the way the AP document page shows them, so the same item invoiced on two POs
   * reads as two separate groups. Lines with no PO (charges, receipts) form one group with an empty reference.
   */
  readonly lineGroups = computed(() => {
    const groups: { reference: string; number: string; lines: ApDocumentLine[] }[] = [];
    for (const line of this.payable()?.document.lineItems ?? []) {
      const reference = line.purchaseOrderKey?.reference ?? '';
      let group = groups.find((g) => g.reference === reference);
      if (!group) {
        group = { reference, number: line.purchaseOrderKey?.number ?? '', lines: [] };
        groups.push(group);
      }
      group.lines.push(line);
    }
    return groups;
  });

  private statusLabels: Record<number, string> = {};
  private typeLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this.statusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
    this.typeLabels = this.enumService.getEnum('ApDocumentType').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;

    if (!this.documentId) {
      this.loading.set(false);
      this.failed.set(true);
      return;
    }

    this.documentService
      .getPayableDetail(this.documentId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.payable.set(response.data ?? null);
          this.failed.set(!response.data);
        },
        error: () => this.failed.set(true),
      });
  }

  get currencyLabel(): string {
    const currency = this.payable()?.document.currency;
    return currency === undefined || currency === null ? '' : (this.currencyLabels[currency] ?? '');
  }

  /** Reporting currency the functional figures are in — read from the document rather than assumed USD. */
  get functionalLabel(): string {
    const currency = this.payable()?.document.functionalCurrency;
    return currency === undefined || currency === null ? '' : (this.currencyLabels[currency] ?? '');
  }

  /**
   * Converts any of this document's amounts at the rate it was booked with.
   *
   * <p>Derived rather than read from <c>functionalAmount</c>, which only exists for the document total: a
   * balance due or a single line has no stored functional counterpart. Because ONE document carries ONE
   * rate, converting the total this way reproduces the posted figure exactly, so the block stays internally
   * consistent instead of mixing a stored total with derived parts.</p>
   */
  functional(amount: number | null | undefined): number {
    return Math.round((amount ?? 0) * (this.payable()?.document.exchangeRate || 1) * 100) / 100;
  }

  get statusLabel(): string {
    const status = this.payable()?.document.status;
    return status === undefined || status === null ? '' : (this.statusLabels[status] ?? '');
  }

  get typeLabel(): string {
    const type = this.payable()?.document.documentType;
    return type === undefined || type === null ? '' : (this.typeLabels[type] ?? '');
  }

  /** True when a note or withholding moved the net away from the document's own balance. */
  get hasNotes(): boolean {
    const p = this.payable();
    return !!p && (p.creditNotes.length > 0 || p.debitNotes.length > 0 || p.withheldAmount > 0);
  }

  onClose(): void {
    this.close({ action: 'Cancel', data: null });
  }
}
